'use client'
import { getFavoritesStorage } from 'favorite/utils/favoritesCache'
import { initializeFavorites } from 'favorite/utils/initializeFavorites'
import { type RefObject, useEffect } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import type { StorageInstance } from 'storage/domain/Storage'
import { isBot } from 'utils/isBot/IsBot'
import { FAVORITES_LIST_KEY } from '../constants/constants'
import type { FavoriteProductResponse } from '../types/types'
import { useSyncFavorites } from './useSyncFavorites'
import { hasLoggedChanged, setIsLogged } from './utils/hasLoggedChanged'

interface UseInitializeFavoritesProps {
	favoritesEnabled: boolean
	hasInitialized: RefObject<boolean>
	onInitialitze(favorites: FavoriteProductResponse[]): void
}

export function useInitializeFavorites({
	favoritesEnabled,
	hasInitialized,
	onInitialitze,
}: UseInitializeFavoritesProps) {
	const { isReady, token, logged } = useSession()
	const { syncFavorites } = useSyncFavorites()

	useEffect(() => {
		if (!(favoritesEnabled && isReady && token)) {
			return
		}

		const hasLoggedChangedValue = hasLoggedChanged(logged)
		if (hasInitialized.current && !hasLoggedChangedValue) {
			return
		}

		hasInitialized.current = true
		const favoritesStorage: StorageInstance | null = getFavoritesStorage(
			logged,
			token
		)

		const init = async () => {
			let favorites: FavoriteProductResponse[] = []

			try {
				if (isBot() || (hasLoggedChangedValue && !logged)) {
					favorites = []
					await favoritesStorage?.addKeyToCache(FAVORITES_LIST_KEY, [])
					setIsLogged(logged)
				} else {
					if (hasLoggedChangedValue) {
						await syncFavorites()
						await favoritesStorage?.addKeyToCache(FAVORITES_LIST_KEY, null)
						setIsLogged(logged)
					}

					favorites = await initializeFavorites({
						token,
						logged,
						hasLoggedChangedValue,
					})
				}
			} finally {
				onInitialitze(favorites)
			}
		}
		void init()
	}, [isReady, token, logged])
}
